import { scrollElementToCenterBelowHeader } from '@/utils/checkoutScroll';

import {
  checkIsOneyPaymentByType,
  isNotSupportMulticardPaymentMethod,
} from '@/lib/payment/paymentTypeChecks';
import type { PaymentType } from '@/lib/payment/types';
import { PAYMENT_TYPES } from '@/lib/payment/types';
import { getRegionFromLocale } from '@/lib/regions';
import type {
  PaymentGatewayConfig,
  PaymentMethodConfiguration,
} from '@/states/paymentGatewaysState';

export { scrollElementToCenterBelowHeader };

const SECURED_FIELD_TYPES: Record<string, string> = {
  'adyen-checkout__field--cardNumber': 'encryptedCardNumber',
  'adyen-checkout__field--expiryDate': 'encryptedExpiryDate',
  'adyen-checkout__field--securityCode': 'encryptedSecurityCode',
};

export const getSecuredFieldType = (target: EventTarget | null) => {
  if (!(target instanceof Element)) {
    return null;
  }

  const field = target.closest('.adyen-checkout__field');
  const fieldClass = Object.keys(SECURED_FIELD_TYPES).find((className) =>
    field?.classList.contains(className),
  );

  return fieldClass ? SECURED_FIELD_TYPES[fieldClass] : null;
};

export const canFocusSecuredField = (
  gatewayCheckout: unknown,
): gatewayCheckout is { setFocusOn: (fieldType: string) => void } =>
  typeof (gatewayCheckout as { setFocusOn?: unknown } | null)?.setFocusOn ===
  'function';

export const checkCountryBillingSameShippingAddressAffirm = (
  billingCountry?: string,
  locale?: string,
  method?: string,
) => {
  if (
    !method ||
    !locale ||
    !billingCountry ||
    method !== PAYMENT_TYPES.AFFIRM
  ) {
    return true;
  }

  const { country } = getRegionFromLocale(locale);

  if (country.code !== billingCountry) return false;

  return true;
};

export const isPaymentMethodDisabled = (
  availablePaymentMethods: PaymentMethodConfiguration[],
  isMulticard?: boolean,
  paymentMethod?: PaymentType | null,
) => {
  let paymentMethodConfiguration = availablePaymentMethods.find(
    (item) => item.type === paymentMethod,
  );

  // Allow multiple oney payment methods to be selected
  if (
    !paymentMethodConfiguration &&
    paymentMethod &&
    checkIsOneyPaymentByType(paymentMethod)
  ) {
    paymentMethodConfiguration = availablePaymentMethods.find((item) =>
      checkIsOneyPaymentByType(item.type),
    );
  }

  if (!paymentMethodConfiguration) {
    return true;
  }

  return (
    (isMulticard &&
      paymentMethodConfiguration?.dropInConfiguration?.splitPayment ===
        false) ||
    (isMulticard && isNotSupportMulticardPaymentMethod(paymentMethod)) ||
    (!isMulticard &&
      paymentMethodConfiguration?.dropInConfiguration?.available === false)
  );
};

export const isPaymentMethodSelectable = (
  availablePaymentMethods: PaymentMethodConfiguration[],
  isMulticard?: boolean,
  paymentMethod?: PaymentType,
  maxPayments?: number,
  limitMax?: number,
  maxAmount?: number,
) => {
  const paymentMethodConfiguration = availablePaymentMethods.find(
    (item) => item.type === paymentMethod,
  );

  if (isMulticard && isNotSupportMulticardPaymentMethod(paymentMethod)) {
    return false;
  }

  if (
    paymentMethodConfiguration &&
    ((isMulticard &&
      paymentMethodConfiguration.dropInConfiguration?.splitPayment === true) ||
      (!isMulticard &&
        paymentMethodConfiguration.dropInConfiguration?.available !== false))
  ) {
    return true;
  }

  if (
    (paymentMethod === PAYMENT_TYPES.AFFIRM ||
      paymentMethod === PAYMENT_TYPES.ZIP ||
      paymentMethod === PAYMENT_TYPES.FACILYPAY_3X ||
      paymentMethod === PAYMENT_TYPES.FACILYPAY_4X ||
      paymentMethod === PAYMENT_TYPES.CEMBRA ||
      paymentMethod === PAYMENT_TYPES.CEMBRA_INVOICE) &&
    isMulticard &&
    maxPayments === 1 &&
    maxAmount &&
    limitMax &&
    Number(maxAmount) > limitMax
  ) {
    return true;
  }
  return false;
};

// Methods capped by the gateway limitMax instead of a checkout config value.
const GATEWAY_CAPPED_PAYMENT_TYPES: Readonly<PaymentType[]> = [
  PAYMENT_TYPES.FACILYPAY_3X,
  PAYMENT_TYPES.FACILYPAY_4X,
  PAYMENT_TYPES.KLARNA_ACCOUNT,
];

const getGatewayLimitMax = (
  type: PaymentType,
  gatewayConfigs: PaymentGatewayConfig[],
) => {
  const limitMax = gatewayConfigs
    .flatMap(({ paymentMethods }) => paymentMethods)
    .find((item) => item.type === type)?.dropInConfiguration?.limitMax;
  return typeof limitMax === 'number' ? limitMax : undefined;
};

const getSinglePaymentMaxAmount = (
  type: PaymentType,
  configMaxAmounts: Partial<Record<PaymentType, number>>,
  gatewayConfigs: PaymentGatewayConfig[],
) => {
  if (type in configMaxAmounts) {
    return configMaxAmounts[type];
  }
  if (GATEWAY_CAPPED_PAYMENT_TYPES.includes(type)) {
    return getGatewayLimitMax(type, gatewayConfigs);
  }
  return undefined;
};

/**
 * BEC-25831: whether a single payment of `amount` is above the method's cap, the
 * same caps as the over-limit error in usePaymentViewEffects.
 */
export const isAboveSinglePaymentMax = ({
  amount,
  configMaxAmounts,
  gatewayConfigs,
  type,
}: {
  amount: number;
  configMaxAmounts: Partial<Record<PaymentType, number>>;
  gatewayConfigs: PaymentGatewayConfig[];
  type: PaymentType;
}) => {
  const maxAmount = getSinglePaymentMaxAmount(
    type,
    configMaxAmounts,
    gatewayConfigs,
  );
  return !!maxAmount && amount > maxAmount;
};

export const filterPaymentMethods = (
  paymentMethods: PaymentMethodConfiguration[],
  locale?: string,
  billingCountryCode?: string,
) => {
  return paymentMethods.reduce((arr, current) => {
    const isAffirmPaymentAvailable =
      checkCountryBillingSameShippingAddressAffirm(
        billingCountryCode,
        locale,
        current.type,
      );
    if (!isAffirmPaymentAvailable) return arr;

    const isOnePayment = checkIsOneyPaymentByType(current.type);
    const hasOnePayment = arr.find((item) =>
      checkIsOneyPaymentByType(item.type),
    );
    if (!isOnePayment || !hasOnePayment) {
      arr.push(current);
    }
    return arr;
  }, [] as PaymentMethodConfiguration[]);
};

const GATEWAY_DROP_IN_READY_MIN_HEIGHT = 150;
const GATEWAY_DROP_IN_FORM_SELECTOR = '.adyen-checkout__card__form';

export const PAYMENT_METHODS_HEADER_ID = 'payment-methods-header';

const getGatewayDropInHeight = (element: HTMLElement) =>
  Math.max(element.offsetHeight, element.scrollHeight);

export const isGatewayDropInReady = (element: HTMLElement) =>
  element.querySelector(GATEWAY_DROP_IN_FORM_SELECTOR) !== null &&
  getGatewayDropInHeight(element) >= GATEWAY_DROP_IN_READY_MIN_HEIGHT;
